/**
 * חיפוש גמיש למוצרים — תואם ל־buildSearchQuery ב־productController:
 * כל "מילה" (חלקים מופרדים ברווח) חייבת להופיע בשם/מקור או (למוצר מתאים) בתווית "נאפה לאחר הפסח".
 * לדוגמה: "בייג נלפ" → בייג בשם + נלפ כתווית נאפה לאחר הפסח.
 * מחרוזת מלאה: ברקוד / מק״ט חיצוני / שם ספק (כמו בשרת).
 *
 * דירוג חיפוש: מילה שלמה (טוקן) עדיפה על תת־מחרוזת — "כריות" לפני "סוכריות".
 */
import {
  isBakedAfterPassoverProduct,
  tokenRefersToBakedAfterPassover,
} from "@/lib/bakedAfterPassoverSearch";
import { getProductCompanyId } from "@/utils/productAvailability";

const lower = (x) => String(x ?? "").toLowerCase();

/**
 * מוצר שמותר להציג בחיפוש מגוון סניף — כל מה שבמגוון, כולל ארכיון/33/ללא מלאי.
 */
export function isProductVisibleInStoreCatalogSearch(product) {
  if (!product) return false;
  if (product.toBeDeleted === true || product.to_be_deleted === true) return false;
  return true;
}

/**
 * מוצר שמותר לשלוח בהזמנה — חסימת סל/שליחה (לא חיפוש).
 * @returns {string|null} הודעה בעברית אם לא זכאי, אחרת null
 */
export function getCartLineOrderBlockReason(product) {
  if (!product) return null;
  if (product.is_archived || product.isArchived) return "לא יישלח בהזמנה";
  const dept = String(
    product.comaxDepartmentCode ?? product.comax_department_code ?? ""
  ).trim();
  if (dept === "33") return "לא יישלח בהזמנה (מחלקה 33 בקומקס)";
  if (product.toBeDeleted === true || product.to_be_deleted === true) {
    return "לא יישלח בהזמנה (מסומן למחיקה)";
  }
  const rawStock = product.stock_quantity ?? product.stockQuantity;
  if (rawStock != null && rawStock !== "") {
    const stock = Number(rawStock);
    if (Number.isFinite(stock) && stock <= 0) {
      return "לא יישלח בהזמנה (אין מלאי במחסן)";
    }
  }
  return null;
}

export function isProductEligibleForCustomerSearch(product) {
  return getCartLineOrderBlockReason(product) == null;
}

/** טוקנים לשם/מקור להשוואת "מילה שלמה" */
export function tokenizeProductSearchText(text) {
  return lower(text)
    .split(/[\s\-_,./|()+״"':;]+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

/** האם `part` מופיע כטוקן שלם באחד השדות (לא רק כחלק מתוך "סוכריות") */
export function partMatchesWholeWordInProductFields(part, product) {
  const pl = String(part ?? "").trim().toLowerCase();
  if (!pl) return false;
  const fields = [product?.name ?? product?.product_name, product?.original_name ?? product?.originalName];
  for (const f of fields) {
    const tokens = tokenizeProductSearchText(f);
    if (tokens.some((t) => t === pl)) return true;
  }
  return false;
}

/**
 * ציון התאמה לחיפוש (גבוה = רלוונטי יותר). מינימום לפי טוקנים — כולם חייבים להתאים;
 * רמת ההתאמה הכי "חלשה" קובעת (כמו מינימום ציון לכל מילה).
 */
export function flexibleProductSearchRank(product, rawSearch) {
  const safe = String(rawSearch ?? "").trim();
  if (!safe) return 0;
  if (!isProductVisibleInStoreCatalogSearch(product)) return -1;

  const parts = safe.split(/\s+/).filter(Boolean);
  const name = lower(product?.name ?? product?.product_name);
  const orig = lower(product?.original_name ?? product?.originalName);
  const barcode = String(product?.barcode ?? "");
  const ext = String(product?.external_id ?? product?.externalId ?? "");
  const sup = String(product?.supplier_name ?? product?.supplierName ?? "");
  const safeLower = safe.toLowerCase();

  if (
    barcode.toLowerCase().includes(safeLower) ||
    ext.toLowerCase().includes(safeLower) ||
    sup.toLowerCase().includes(safeLower)
  ) {
    return 1_000_000;
  }

  const scoreOnePart = (part) => {
    const pl = String(part).toLowerCase();
    if (!pl) return 0;
    if (isBakedAfterPassoverProduct(product) && tokenRefersToBakedAfterPassover(part)) {
      return 450;
    }
    if (partMatchesWholeWordInProductFields(part, product)) return 1000;
    if (name.includes(pl) || orig.includes(pl)) return 100;
    if (sup.toLowerCase().includes(pl)) return 80;
    return 0;
  };

  const scores = parts.map(scoreOnePart);
  return scores.length ? Math.min(...scores) : 0;
}

function promotedCompanySortKey(product, promotedSet) {
  if (!promotedSet?.size) return 0;
  const cid = getProductCompanyId(product);
  return cid && promotedSet.has(cid) ? 1 : 0;
}

export function compareFlexibleSearchRelevance(a, b, rawSearch, promotedCompanyIds) {
  const safe = String(rawSearch ?? "").trim();
  const ra = flexibleProductSearchRank(a, safe);
  const rb = flexibleProductSearchRank(b, safe);
  if (rb !== ra) return rb - ra;
  const promotedSet =
    promotedCompanyIds?.length > 0
      ? new Set(promotedCompanyIds.map(String))
      : null;
  if (promotedSet?.size) {
    const pa = promotedCompanySortKey(a, promotedSet);
    const pb = promotedCompanySortKey(b, promotedSet);
    if (pb !== pa) return pb - pa;
  }
  const na = String(a?.name ?? a?.product_name ?? "");
  const nb = String(b?.name ?? b?.product_name ?? "");
  return na.localeCompare(nb, "he");
}

/** עותק ממוין — לא משנה את המערך המקורי */
export function sortProductsByFlexibleSearchRelevance(products, rawSearch, promotedCompanyIds) {
  const safe = String(rawSearch ?? "").trim();
  if (!Array.isArray(products)) return products ?? [];
  if (!safe) return products;
  const filtered = products.filter(isProductVisibleInStoreCatalogSearch);
  if (filtered.length < 2) return filtered;
  return [...filtered].sort((a, b) =>
    compareFlexibleSearchRelevance(a, b, safe, promotedCompanyIds)
  );
}

/**
 * @param {{ skipCustomerEligibility?: boolean }} [options] — במסכי ניהול (מוצרים מלאי וכו') יש להעביר `skipCustomerEligibility: true` כדי לא להסתיר מוצר עם מלאי מחסן 0
 */
export function matchesFlexibleProductSearch(product, rawSearch, options = {}) {
  const safe = String(rawSearch ?? "").trim();
  if (!safe) return true;
  if (
    !options.skipCustomerEligibility &&
    !isProductVisibleInStoreCatalogSearch(product)
  ) {
    return false;
  }

  const parts = safe.split(/\s+/).filter(Boolean);
  const name = lower(product.name ?? product.product_name);
  const orig = lower(product.original_name ?? product.originalName);
  const barcode = String(product.barcode ?? "");
  const ext = String(product.external_id ?? product.externalId ?? "");
  const sup = String(product.supplier_name ?? product.supplierName ?? "");

  const safeLower = safe.toLowerCase();
  const idMatches =
    barcode.toLowerCase().includes(safeLower) ||
    ext.toLowerCase().includes(safeLower) ||
    sup.toLowerCase().includes(safeLower);
  if (idMatches) return true;

  const partMatches = (part) => {
    const pl = String(part).toLowerCase();
    if (!pl) return false;
    if (name.includes(pl) || orig.includes(pl) || sup.toLowerCase().includes(pl)) return true;
    if (isBakedAfterPassoverProduct(product) && tokenRefersToBakedAfterPassover(part)) return true;
    return false;
  };

  return parts.length > 0 && parts.every(partMatches);
}
